<template>
    <div class="appointments-choose-date">
        <div class="appointments-choose-date__title">
            Выберите {{ action === 2 ? 'новую' : '' }} дату
        </div>
        <div class="appointments-choose-date__description">
            Выберите {{ action === 2 ? 'новую' : '' }} удобную для вас дату посещения офиса
        </div>

        <div class="appoitments-choose-date__today">
            <strong>Хотите записаться на сегодня?</strong> <br>Позвоните по телефону <a href="tel:+74951521328">+7 495 152-13-28</a>
        </div>

        <form class="appointments-choose-date__form"
              @submit.prevent="triggerGuard">
            <template v-if="isDatesFetching">
                <div class="appointments-choose-date__loading">
                    <IconLoadGrey class="appointments-choose-date__loader" />
                    <div class="appointments-choose-date__loading-text">
                        Ищем свободные даты...
                    </div>
                </div>
            </template>
            <template v-else-if="isDatesFetchingError">
                К сожалению, произошла техническая ошибка.
                <br>
                Попробуйте еще раз позже или перезагрузите страницу.
            </template>
            <template v-else-if="!dateList?.length">
                Свободные даты для записи отсутствуют.
            </template>

            <div v-show="!isDatesFetching && !isDatesFetchingError && dateList?.length"
                 class="appointments-choose-date__date-picker">
                <STDatePickerWrapper
                    id="appointments-datepicker"
                    :value="currentDate"
                    :active-dates="dateList"
                    name="appointments-datepicker"
                    label="Дата посещения"
                    @change="changeDate" />
            </div>

            <div class="appointments-choose-date__time-picker">
                <div class="appointments-choose-date__title">
                    Выберите {{ action === 2 ? 'новое' : '' }} время
                </div>

                <template v-if="!currentDate">
                    Выберите {{ action === 2 ? 'новую' : '' }} дату посещения для просмотра доступных временных слотов
                </template>
                <template v-else>
                    <template v-if="isTimesFetching">
                        <div class="appointments-choose-date__loading">
                            <IconLoadGrey class="appointments-choose-date__loader" />
                            <div class="appointments-choose-date__loading-text">
                                Ищем свободные слоты...
                            </div>
                        </div>
                    </template>
                    <template v-else-if="isTimesFetchingError">
                        К сожалению, произошла техническая ошибка.
                        <br>
                        Попробуйте еще раз позже или перезагрузите страницу.
                    </template>
                    <template v-else-if="!timeList?.length">
                        Свободные слоты для записи отсутствуют.
                    </template>
                    <STTimeList v-else
                                :list="timeList"
                                :value="currentTime"
                                @click="selectTime" />
                </template>
            </div>

            <div class="appointments-choose-date__agency">
                <div class="appointments-choose-date__title">
                    Укажите название агентства
                </div>
                <div class="appointments-choose-date__agency-input">
                    <STInput
                        id="appointments-agency-input"
                        v-model.trim="agency"
                        :modify="{'is-error': $v.agency.$dirty && $v.agency.$invalid}"
                        :error="getErrorText($v.agency)"
                        name="appointments-agency-input"
                        label="Название агентства"
                        @input="$v.agency.$touch()"
                        @focus="$v.agency.$touch()" />
                </div>
            </div>

            <div class="appointments-choose-date__comment">
                <div class="appointments-choose-date__title">
                    Укажите информацию для заказа пропуска в&nbsp;офис продаж
                </div>

                <div class="appointments-choose-date__description">
                    Необходимо указать: <br>
                    - ФИО всех участников встречи (агент, клиент, сопровождающие); <br>
                    - марку и&nbsp;гос номер автомобиля (если автомобилей будет несколько, необходимо указать&nbsp;данные&nbsp;на&nbsp;каждый&nbsp;автомобиль).
                </div>

                <div class="appointments-choose-date__description">
                    <b>Для прохода в&nbsp;Офис продаж в&nbsp;Бизнес-центре необходимо иметь при себе документ, удостоверяющий личность.</b>
                </div>

                <div class="appointments-choose-date__comment-input">
                    <Textarea id="appointments-choose-date-comment"
                              v-model.trim="comment"
                              :modify="{'is-error': $v.comment.$dirty && $v.comment.$invalid}"
                              :error="getErrorText($v.comment)"
                              name="appointments-choose-date-comment"
                              label="Введите необходимую информацию"
                              @input="$v.comment.$touch()"
                              @focus="$v.comment.$touch()" />
                </div>
            </div>

            <div class="appointments-choose-date__actions">
                <STBaseButton v-show="action === 1"
                              text="Назад"
                              icon-before="IconRightFill"
                              icon-before-modify="icon-fill icon_color_blue"
                              modify="button_theme_white button_size_small icon-rotate"
                              @click="back" />

                <STBaseButton :text="action === 2 ? 'Перенести запись' : 'Далее'"
                              icon-after="IconRightFill"
                              icon-after-modify="icon-fill icon_color_white"
                              icon-load="IconLoad"
                              modify="button_size_small"
                              :icon-load-id="getRandomId"
                              :disabled="!dateWithTime || !comment || $v.comment.$invalid || $v.agency.$invalid || !agency"
                              :is-loading="isLoading"
                              @click="submit" />
            </div>
        </form>
    </div>
</template>

<script>
import Utils from '@/helpers/Utils';
import IconRightFill from '@/components/Icons/RightFill';
import IconLoad from '@/components/Icons/Load';
import IconLoadGrey from '@/components/Icons/LoadGrey';
import Textarea from '@/components/FormsElements/Textarea/Textarea';
import {maxLength, minLength, required} from 'vuelidate/lib/validators';

import moment from 'moment-timezone';

import {mapActions} from 'vuex';

export default {
    name: 'AppointmentsChooseDate',

    components: {
        IconLoadGrey,
        Textarea
    },

    props: {
        /*
        * 0 - удалить запись
        * 1 - добавить запись
        * 2 - изменить запись
        */
        action: {
            type   : [Number, undefined],
            default: 1
        },
        projectId      : [String],
        clientPhone    : [String],
        visitRecordId  : [String, undefined], // для action 2
        externalComment: [String],
        clientName     : [String],
        clientSurname  : [String]
    },

    validations: {
        comment: {
            required,
            minLength: minLength(10),
            maxLength: maxLength(700)
        },

        agency: {
            required,
            minLength: minLength(2),
            maxLength: maxLength(200)
        }
    },

    data() {
        return {
            isDatesFetching     : true,
            isDatesFetchingError: false,
            isTimesFetching     : false,
            isTimesFetchingError: false,
            isLoading           : false,
            dateList            : [], // массив свободных дат
            timeList            : [], // массив свободных слотов времени
            currentDate         : '', // выбрання дата
            currentTime         : '', // выбранное время
            today               : '', // сегодняшняя дата и время
            comment             : '', // комментарий
            agency              : '', // название агентства
        };
    },

    computed: {
        /**
         * Формирует уникальный идентификатор для иконки лоадера
         * @return {string}
         */
        getRandomId() {
            return '-icon-load-' + Utils.generateRandomValue() + '-' + Utils.generateRandomValue();
        },

        dateWithTime() {
            if (!this.currentDate || !this.currentTime) {
                return null;
            }

            return `${moment(this.currentDate).format('YYYY-MM-DD')} ${this.currentTime}`;
        }
    },

    created() {
        if (this.externalComment) {
            this.comment = this.externalComment;
        }
    },

    async mounted() {
        if (this.action === 0) {
            console.error('Запрещено выбирать дату для удаления записи');
            this.back();
        }

        if (this.action === 1 && (!this.projectId || !this.clientPhone)) {
            console.error('Запрещено выбирать дату создания новой записи без projectId и номера клиента');
            this.back();
        }

        if (this.action === 2 && !this.visitRecordId) {
            console.error('Запрещено выбирать дату переноса записи без visitRecordId');
            this.back();
        }

        await this.getDates();
    },

    methods: {
        ...mapActions(['getAppointmentsDates', 'getAppointmentsTimeSlots', 'createAppointment', 'changeAppointment']),

        getErrorText(value) {
            switch (false) {
                case value.required:
                    return 'Это обязательное поле';
                case value.minLength:
                    return `Минимальная длина ${value.$params?.minLength?.min} символа`;
                case value.maxLength:
                    return `Максимальная длина длина ${value.$params?.maxLength?.max} символов`;
            }
        },

        /**
         * Селект при открытии триггерит сабмит формы
         * Данный метод блокирует случайные встроенные сабмиты не по кнопке
         * @return {null}
         */
        triggerGuard() {
            return null;
        },

        /**
         * Получает и устанавливает список дат для записи на приёмку
         */
        async getDates() {
            this.isDatesFetching = true;
            this.isDatesFetchingError = false;

            try {
                const result = await this.getAppointmentsDates({
                    projectId: this.projectId
                });

                this.isDatesFetching = false;

                this.dateList = result?.data?.freeSlotDates?.length ?
                    [...new Set(result.data.freeSlotDates.map(item => item.date))] :
                    [];
                this.today = moment().tz('Europe/Moscow');
            } catch (error) {
                this.dateList = [];
                this.isDatesFetching = false;
                this.isDatesFetchingError = true;
                console.error('Ошибка получения дат на запись на презентацию:', JSON.stringify(error));
            }
        },

        /**
         * Изменяет дату приёмки
         * @param date
         */
        async changeDate(date) {
            this.currentTime = null;
            this.currentDate = date;
            await this.getTimeSlots(date);
        },

        /**
         * Получает слоты времени для записи
         */
        async getTimeSlots(selectDate) {
            this.isTimesFetching = true;
            this.isTimesFetchingError = false;

            try {
                const date = moment(selectDate).format('YYYY-MM-DD');
                const result = await this.getAppointmentsTimeSlots({
                    projectId: this.projectId,
                    date
                });

                this.isTimesFetching = false;

                if (!result?.data?.freeSlots?.length) {
                    this.timeList = [];

                    return;
                }

                // Если выбрана сегодняшняя дата, исключаем слоты с пройденным временем
                // иначе выводим все слоты времени
                if (date === this.today.format('YYYY-MM-DD')) {
                    const allTimeList = [...new Set(result.data.freeSlots.map(item => ({ time: item.timeStart})))];
                    const currentTime = this.today.format('HH:mm');

                    this.timeList = allTimeList.reduce((result, itemTime) => {
                        if (itemTime.time > currentTime) {
                            result.push(itemTime);
                        }

                        return result;
                    }, []);
                } else {
                    this.timeList = [...new Set(result.data.freeSlots.map(item => ({ time: item.timeStart})))];
                }
            } catch (error) {
                this.timeList = [];
                this.isTimesFetching = false;
                this.isTimesFetchingError = true;
                console.error('Ошибка получения времени на запись на презентацию:', JSON.stringify(error));
            }
        },

        /**
         * Устанавливает время для приёмки
         * @param time
         */
        selectTime(time) {
            this.currentTime = time;
        },

        back() {
            this.$emit('back');
        },

        /**
         * Отправляет запрос на проверку уникальности юзера
         */
        async submit() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            if (!this.dateWithTime) {
                console.error('Для создания записи необходимо выбрать дату и время записи');
                return;
            }

            this.isLoading = true;

            try {
                const result = this.action === 1 ?
                    await this._createAppointment() :
                    this.action === 2 ?
                        await this._changeAppointment() :
                        null;
                this.isLoading = false;

                if (result?.data?.messageCode === 1) {
                    const phone = this.clientPhone;
                    this.$emit('recordCreated', {
                        projectId    : this.projectId,
                        clientPhone  : `+7 (${phone.slice(1, 4)}) ${phone.slice(4, 7)}-${phone.slice(7, 9)}-${phone.slice(9)}`,
                        recordDate   : `${moment(this.currentDate).locale('ru').format('DD MMMM YYYY')} в ${this.currentTime}`,
                        clientName   : this.clientName,
                        clientSurname: this.clientSurname,
                        comment      : this.comment
                    });
                } else {
                    console.error('Ошибка создания записи на презентацию. MessageCode: ',
                        result?.data?.messageCode,
                        '. Message: ',
                        result?.data?.message
                    );
                    this.$emit('recordNotCreated');
                }
            } catch (error) {
                this.isLoading = false;
                console.error('Ошибка создания записи на презентацию:', JSON.stringify(error));
                this.$emit('recordNotCreated');
            }
        },

        async _createAppointment() {
            if (!this.projectId || !this.dateWithTime || !this.clientPhone) {
                return null;
            }

            return await this.createAppointment({
                projectId     : this.projectId,
                visitStartTime: this.dateWithTime,
                clientPhone   : this.clientPhone,
                clientName    : this.clientName,
                clientSurname : this.clientSurname,
                comment       : this.comment,
                agency        : this.agency
            });
        },

        async _changeAppointment() {
            if (!this.projectId && !this.visitRecordId && !this.dateWithTime) {
                return null;
            }

            return await this.changeAppointment({
                projectId     : this.projectId,
                visitRecordId : this.visitRecordId,
                visitStartTime: this.dateWithTime,
                comment       : this.comment
            });
        }
    }
};
</script>

<style lang="scss">
@import "AppointmentsChooseDate";
</style>
